import { estimateFlight } from '@trips/shared/flight-estimate';
import { flightStatus, type LiveExtras } from '@trips/shared/flight-status';
import { flightMilestones } from '@trips/shared/flight-times';
import {
  formatDateRange,
  formatDuration,
  formatRelativeDay,
  formatTime,
} from '@trips/shared/format';
import { flightLabel } from '@trips/shared/items';
import type { NowState } from '@trips/shared/now';
import type { ReactNode } from 'react';
import { selectNow, selectPrefs, selectToday } from '../../data/selectors';
import { useStore } from '../../data/store';
import { now } from '../../lib/clock';
import { phoneLink } from '../../lib/link';
import { Link } from '../../lib/router';
import { useLiveFlight } from '../../lib/useLiveFlight';
import s from './NowCard.module.css';

function Card({
  to,
  label,
  aside,
  title,
  children,
}: {
  to: string;
  label: string;
  aside: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <Link to={to} className={s.card}>
      <span className={s.top}>
        <span>{label}</span>
        <span className="num">{aside}</span>
      </span>
      <span className={s.title}>{title}</span>
      {children}
    </Link>
  );
}

/** The one card at the top: what's happening now, or next. */
export function NowCard() {
  const state = useStore(selectNow);
  const prefs = useStore(selectPrefs);
  const today = useStore(selectToday);
  const lives = useStore((st) => st.tables.flightLive);
  useStore((st) => st.minute);
  // Before takeoff and in the air, keep the flight on this card fresh while there's signal.
  useLiveFlight('item' in state ? state.item : undefined);
  return <NowBody state={state} prefs={prefs} today={today} t={now()} lives={lives} />;
}

function NowBody({
  state,
  prefs,
  today,
  t,
  lives,
}: {
  lives: ReadonlyMap<string, LiveExtras>;
  state: NowState;
  prefs: ReturnType<typeof selectPrefs>;
  today: string;
  t: number;
}) {
  if (state.kind === 'in-flight') {
    const tz = state.to?.tz ?? 'UTC';
    const ll = (p: typeof state.to) =>
      p?.lat != null && p.lon != null ? { lat: p.lat, lon: p.lon } : null;
    const est =
      state.item.startAt != null
        ? estimateFlight(
            { startAt: state.item.startAt, endAt: state.item.endAt },
            lives.get(state.item.id),
            t,
            { from: ll(state.from), to: ll(state.to) },
            phoneLink(),
          )
        : null;
    const lands = est?.on ?? state.arrivesAt;
    const progress = est?.progress ?? state.progress;
    return (
      <Card
        to={`/flights/${state.item.id}`}
        label="In the air"
        aside={`Lands ${formatTime(lands, tz, prefs)}`}
        title={`To ${state.to?.city ?? state.to?.iata ?? 'destination'}`}
      >
        <span className={s.line}>
          {flightLabel(state.item)}, {formatDuration(Math.max(0, (lands - t) / 60_000))} to go
        </span>
        <span className={s.bar}>
          <span style={{ width: `${Math.round(progress * 100)}%` }} />
        </span>
      </Card>
    );
  }

  if (state.kind === 'travel-day') {
    const i = state.item;
    const tz = i.startTz ?? state.from?.tz ?? 'UTC';
    const d = i.details.type === 'flight' ? i.details : null;
    const fs = flightStatus(
      { startAt: i.startAt ?? state.departsAt, endAt: i.endAt, cancelled: i.status === 'cancelled' },
      lives.get(i.id),
      t,
      undefined,
      phoneLink(),
    );
    const departsAt = fs.depart.best;
    const boarding = d
      ? flightMilestones({
          scheduledDepart: i.startAt ?? state.departsAt,
          expectedDepart: departsAt,
          airline: d.airline,
          international:
            !!state.from?.countryCode &&
            !!state.to?.countryCode &&
            state.from.countryCode !== state.to.countryCode,
          boardingAt: d.boardingAt,
        }).find((m) => m.key === 'boarding')
      : undefined;
    return (
      <Card
        to={`/flights/${i.id}`}
        label="Today"
        aside={
          fs.tone === 'ok'
            ? `Leaves in ${formatDuration(Math.max(0, (departsAt - t) / 60_000))}`
            : fs.label
        }
        title={`${state.from?.iata ?? ''} to ${state.to?.iata ?? ''}`}
      >
        <span className={s.line}>
          {flightLabel(i)}, departs {formatTime(departsAt, tz, prefs)}
          {boarding ? `, boards ${formatTime(boarding.at, tz, prefs)}` : ''}
        </span>
      </Card>
    );
  }

  if (state.kind === 'on-trip') {
    const { trip } = state;
    // The trip's own name; it's the city unless I've named it.
    const name = trip.title;
    const day = trip.dayNumber ?? 1;
    // How far through the trip, counting today as underway.
    const share = trip.days > 0 ? Math.min(1, Math.max(0, (day - 0.5) / trip.days)) : 0;
    return (
      <Card to={`/trips/${trip.id}`} label="Now" aside={`Day ${day} of ${trip.days}`} title={name}>
        <span
          className={s.bar}
          role="progressbar"
          aria-label="Trip progress"
          aria-valuemin={0}
          aria-valuemax={trip.days}
          aria-valuenow={day}
        >
          <span style={{ width: `${Math.round(share * 100)}%` }} />
        </span>
      </Card>
    );
  }

  if (state.kind === 'upcoming') {
    const { trip } = state;
    return (
      <Card
        to={`/trips/${trip.id}`}
        label="Next trip"
        aside={trip.startDate ? formatRelativeDay(trip.startDate, today) : ''}
        title={trip.title}
      >
        {trip.startDate && trip.endDate && (
          <span className={s.line}>{formatDateRange(trip.startDate, trip.endDate, prefs)}</span>
        )}
      </Card>
    );
  }

  return null;
}
